'use client';

import { useEffect, useState } from 'react';
import useRemainTimer from 'src/hooks/useRemainTimer';
import { momentTimezone } from 'src/utils/time';

import LoadingSpinner from '@/app/(football)/[team]/_components/common/LoadingSpinner';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import type { TopCollectorSeason } from '@/store/server/common/football/football.dto';
import { useGetTopCollectorPeriodQuery } from '@/store/server/common/football/football.query';
import { useGetOriginalsInitQuery } from '@/store/server/common/originals/originals.query';

interface TimerSectionProps {
  season: TopCollectorSeason;
}

export default function TimerSection({ season }: TimerSectionProps) {
  const { packName, originalsName } = useTeamConfig();

  const [isTimerLoading, setIsTimerLoading] = useState(true);
  const {
    remainTime: { days: remainDays, hours: remainHours, minutes: remainMinutes, seconds: remainSeconds },
    startTimer,
    endTimer,
  } = useRemainTimer();

  const { data: initData } = useGetOriginalsInitQuery({
    originalsNameList: [originalsName],
  });

  const { data: periodData } = useGetTopCollectorPeriodQuery({
    packIdx: initData?.[originalsName]?.packList.find((p) => p.packName === packName)?.packIdx ?? 0,
    season,
  });

  const isTimerEnd = remainDays === '00' && remainHours === '00' && remainMinutes === '00' && remainSeconds === '00';

  /* eslint-disable react-hooks/set-state-in-effect -- timer loading state from async data */
  useEffect(() => {
    if (!periodData) return;

    try {
      const endDttm = periodData.endDateTime;

      if (!endDttm) {
        endTimer();
        setIsTimerLoading(false);
        return;
      }

      const now = momentTimezone();
      const endTime = momentTimezone(endDttm);

      if (endTime.isBefore(now)) {
        endTimer();
        setIsTimerLoading(false);
        return;
      }

      startTimer({ endTime });
      setIsTimerLoading(false);
    } catch (error) {
      console.error(error);
    }
  }, [periodData, startTimer, endTimer]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <section className="flex w-full flex-col items-center">
      {/* Timer Loading */}
      {isTimerLoading && (
        <div className="flex h-[66px] w-full items-center justify-center">
          <LoadingSpinner size={28} />
        </div>
      )}

      {/* Timer Loaded */}
      {!isTimerLoading && (
        <div className="flex w-full flex-col items-center gap-2">
          {/* Season In Progress Timer */}
          {!isTimerEnd && (
            <>
              <span className="typo-body-5 text-g-00">
                시즌 종료까지
              </span>
              <div className="flex items-center justify-center gap-2">
                <div className="flex items-center gap-0.5 flex-col w-8">
                  <span className="typo-heading-3 text-g-00">
                    {remainDays}
                  </span>
                  <span className="typo-body-8 text-g-00">일</span>
                </div>
                <span className="typo-heading-3 text-g-00">:</span>
                <div className="flex items-center gap-0.5 flex-col w-8">
                  <span className="typo-heading-3 text-g-00">
                    {remainHours}
                  </span>
                  <span className="typo-body-8 text-g-00">시</span>
                </div>
                <span className="typo-heading-3 text-g-00">:</span>
                <div className="flex items-center gap-0.5 flex-col w-8">
                  <span className="typo-heading-3 text-g-00">
                    {remainMinutes}
                  </span>
                  <span className="typo-body-8 text-g-00">분</span>
                </div>
                <span className="typo-heading-3 text-g-00">:</span>
                <div className="flex items-center gap-0.5 flex-col w-8">
                  <span className="typo-heading-3 text-g-00">
                    {remainSeconds}
                  </span>
                  <span className="typo-body-8 text-g-00">초</span>
                </div>
              </div>
            </>
          )}

          {/* Season Ended Text */}
          {isTimerEnd && (
            <div className="flex flex-col items-center gap-2 w-full">
              <span className="typo-body-1 text-g-00">
                시즌 종료
              </span>
              <div className="flex flex-col items-center">
                <span className="typo-body-6 text-g-00">
                  탑콜렉터 순위가 확정되었어요!
                </span>
                <span className="typo-body-6 text-g-00">
                  혜택 안내 문자가 1주일 내 발송될 예정이에요.
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
